import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { COMPANY } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import EnquiryForm from "@/components/EnquiryForm";
import CallbackDialog from "@/components/CallbackDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Satellite, ShieldAlert, Route, MapPin, Smartphone, BarChart3, Wrench, Headphones,
  ArrowRight, CheckCircle2, ShoppingBag, PhoneCall, PlayCircle, Star, Truck, School, Bike, Car, Building2, Factory,
} from "lucide-react";

const BENEFITS = [
  { icon: Satellite, title: "Real-Time Tracking", desc: "Live GPS location every 10 seconds with route history." },
  { icon: ShieldAlert, title: "Anti-Theft Alerts", desc: "SOS, ignition, tamper and unauthorised movement alerts." },
  { icon: Route, title: "Trip History & Playback", desc: "Replay any trip with speed, stops and route details." },
  { icon: MapPin, title: "Geofencing", desc: "Draw zones and get instant entry / exit notifications." },
  { icon: Smartphone, title: "Mobile App Access", desc: "iOS & Android app for live tracking on the go." },
  { icon: BarChart3, title: "Fleet Reports", desc: "Detailed reports for fuel, distance, driver behaviour." },
  { icon: Wrench, title: "Easy Installation", desc: "Professional installation across Rajasthan and pan-India." },
  { icon: Headphones, title: "Technical Support", desc: "Multi-language technical support 7 days a week." },
];

const STEPS = [
  { n: "01", title: "Select Your Solution", desc: "Choose the right GPS device or fleet plan." },
  { n: "02", title: "Place Your Order", desc: "Simple checkout with secure Indian payments." },
  { n: "03", title: "Installation", desc: "Our technicians install & configure the device." },
  { n: "04", title: "Start Tracking", desc: "Track live from the KSPB web dashboard & app." },
];

const INDUSTRIES = [
  { icon: Truck, label: "Logistics & Transport" },
  { icon: School, label: "School Buses" },
  { icon: Bike, label: "Delivery Fleets" },
  { icon: Car, label: "Personal Vehicles" },
  { icon: Building2, label: "Corporate Fleets" },
  { icon: Factory, label: "Construction & Heavy" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cbOpen, setCbOpen] = useState(false);
  const [cbProduct, setCbProduct] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const openCallback = (p) => { setCbProduct(p); setCbOpen(true); };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative hero-radial text-white overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs font-semibold tracking-widest uppercase">
              <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
              Trusted GPS partner in India
            </div>
            <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Smart GPS Tracking for <span className="text-[#FF6D00]">Safer</span> & More <span className="text-[#FF6D00]">Efficient</span> Vehicles
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl">
              Track, manage and protect your vehicles with reliable GPS technology from {COMPANY.name}. Real-time location, anti-theft alerts, fleet reports and dedicated Indian support — all in one platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-buy-tracker" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF6D00] font-semibold hover:bg-[#ff8433] active:scale-95 transition-transform">
                <ShoppingBag className="w-4 h-4" /> Buy GPS Tracker
              </Link>
              <Link to="/contact" data-testid="hero-quote" className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/30 bg-white/5 backdrop-blur hover:bg-white/10 transition-colors">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => openCallback(null)} data-testid="hero-demo" className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white/90 hover:text-white transition-colors">
                <PlayCircle className="w-5 h-5" /> Request a Demo
              </button>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-xs text-slate-400">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> Secure Indian payments</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> Pan-India installation</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> 4G LTE + 2G fallback</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-[#FF6D00]/25 to-transparent blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1548345680-f5475ea5df84?w=1200&q=80"
                alt="GPS tracking dashboard"
                className="w-full h-[440px] object-cover"
              />
              <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-[#0A192F]/80 backdrop-blur border border-white/10 p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#FF6D00]/20 flex items-center justify-center">
                  <Satellite className="w-6 h-6 text-[#FF6D00]" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-400">Live fleet</div>
                  <div className="font-heading text-lg font-semibold">14 vehicles tracked in real-time</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Uptime</div>
                  <div className="font-heading font-bold text-[#00E676]">99.9%</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-slate-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto py-5">
          <div className="flex whitespace-nowrap overflow-hidden">
            <div className="marquee-inner flex gap-14 items-center text-slate-500 text-sm uppercase tracking-widest font-semibold">
              {["Fleet Management", "School Bus Safety", "Logistics", "Anti-Theft", "Geofencing", "Fuel Monitoring", "Driver Scoring", "24×7 Support", "Fleet Management", "School Bus Safety", "Logistics", "Anti-Theft", "Geofencing", "Fuel Monitoring"].map((t, i) => (
                <span key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF6D00]" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="eyebrow">Why choose KSPB</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] mt-3">
            Everything you need to run a smarter, safer fleet.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="p-6 rounded-2xl border border-slate-200 bg-white tile-hover" data-testid={`benefit-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FF6D00]/10 text-[#FF6D00] flex items-center justify-center mb-4">
                <b.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-[#0A192F]">{b.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow">Featured devices</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-3">Popular GPS Trackers</h2>
            </div>
            <Link to="/products" className="text-sm font-semibold text-[#0A192F] hover:text-[#FF6D00] inline-flex items-center gap-2 transition-colors">
              View all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} onCallback={openCallback} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0A192F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow">How it works</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">Get tracking in 4 simple steps</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative p-6 rounded-2xl border border-white/10 bg-white/5">
                <div className="font-heading text-5xl font-bold text-[#FF6D00]/40">{s.n}</div>
                <h3 className="font-heading text-lg font-semibold mt-3">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-2">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-white/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow">Solutions for every industry</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-3">Built for Indian businesses of every size</h2>
            <p className="text-slate-600 mt-4">Whether you own a single car or manage a fleet of thousands, KSPB offers tailored GPS solutions with local support in Hindi and English.</p>
            <Link to="/industries" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6D00] hover:gap-3 transition-all">
              Explore industry solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {INDUSTRIES.map((it) => (
              <div key={it.label} className="p-5 rounded-xl border border-slate-200 bg-white hover:border-[#FF6D00] transition-colors">
                <it.icon className="w-6 h-6 text-[#0A192F]" />
                <div className="mt-3 font-semibold text-sm text-[#0A192F]">{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-slate-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="eyebrow">Customer stories</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-3">Trusted across Rajasthan and beyond</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-2xl bg-white border border-slate-200" data-testid={`testimonial-${t.id}`}>
                  <div className="flex gap-1 text-[#FF6D00]">
                    {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FF6D00]" />)}
                  </div>
                  <p className="mt-4 text-slate-700 leading-relaxed">"{t.quote}"</p>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <div className="font-semibold text-[#0A192F]">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role} · {t.city}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT + FAQ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="eyebrow">Quick answers</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-3">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="home-faq">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-slate-200">
                <AccordionTrigger className="text-left font-semibold text-[#0A192F] hover:text-[#FF6D00] hover:no-underline">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/faq" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6D00]">
            All FAQs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <EnquiryForm title="Get a Free Quote" subtitle="Tell us about your vehicles and we'll craft the right GPS plan for you." />
      </section>

      <CallbackDialog open={cbOpen} onOpenChange={setCbOpen} product={cbProduct} />
    </div>
  );
}
